import { type FC } from "react";
import { useTranslations } from "next-intl";

import { InputError } from "@/ui/components/form/InputError";
import { Label } from "@/ui/components/form/Label";
import type {
  TFieldsetProps,
} from "@/ui/components/form/Fieldset/Fieldset.config";
import { useFieldset } from "@/ui/components/form/Fieldset/useFieldset";
import { Stack } from "@/ui/components/layout/Stack";
import { cn } from "@/ui/lib/utils";

export const Fieldset: FC<TFieldsetProps> = (
  {
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    children,
    className,
    error,
    errorId,
    label,
    labelId,
    required,
    hideLabel,
    ...props
  },
) => {
  const {
    describedBy,
    errorId: resolvedErrorId,
    labelId: resolvedLabelId,
  } = useFieldset({
    ariaDescribedBy,
    error,
    errorId,
    labelId,
  });
  const t = useTranslations("Shared.Forms");

  return (
    <fieldset
      {...props}
      aria-describedby={describedBy}
      aria-invalid={error ? true : ariaInvalid}
      data-component="Fieldset"
      className={cn("w-full", className)}
    >
      <legend id={resolvedLabelId} className="mb-4">
        <Label className={hideLabel ? "sr-only" : undefined }>
          { label } { required === false ? `(${t("optional")})` : null }
        </Label>
      </legend>
      <Stack>
        { children }
        { error ? (
          <div id={resolvedErrorId}>
            <InputError>{ error }</InputError>
          </div>
        ) : null }
      </Stack>
    </fieldset>
  );
};

Fieldset.displayName = "Fieldset";
